import { supabase } from "@/integrations/supabase/client";
import { AiPersona, AiPersonaFormData } from "@/types/aiPersona";

const transformPersona = (row: any): AiPersona => ({
  id: row.id,
  name: row.name,
  slug: row.slug,
  description: row.description,
  avatarUrl: row.avatar_url,
  defaultModel: row.default_model,
  isActive: row.is_active,
  metadata: row.metadata || {},
  createdBy: row.created_by,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  templateCount: row.template_count,
});

// ------- CRUD -------

export const fetchAiPersonas = async (): Promise<AiPersona[]> => {
  // Fetch personas with template counts via a left join subquery
  const { data, error } = await supabase
    .from("ai_personas" as any)
    .select("*, ai_prompt_templates(count)")
    .order("created_at", { ascending: false });

  if (error) throw error;

  return (data || []).map((row: any) => ({
    ...transformPersona(row),
    templateCount: row.ai_prompt_templates?.[0]?.count ?? 0,
  }));
};

export const fetchAiPersona = async (
  id: number
): Promise<AiPersona | null> => {
  const { data, error } = await supabase
    .from("ai_personas" as any)
    .select("*, ai_prompt_templates(count)")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }

  if (!data) return null;

  return {
    ...transformPersona(data),
    templateCount: (data as any).ai_prompt_templates?.[0]?.count ?? 0,
  };
};

export const createAiPersona = async (
  formData: AiPersonaFormData
): Promise<AiPersona> => {
  const { data, error } = await supabase
    .from("ai_personas" as any)
    .insert({
      name: formData.name,
      slug: formData.slug,
      description: formData.description || null,
      avatar_url: formData.avatarUrl || null,
      default_model: formData.defaultModel || null,
      is_active: formData.isActive ?? true,
      metadata: formData.metadata || {},
    })
    .select()
    .single();

  if (error) throw error;
  if (!data) throw new Error("No data returned after insert");
  return transformPersona(data);
};

export const updateAiPersona = async (
  id: number,
  formData: Partial<AiPersonaFormData>
): Promise<AiPersona> => {
  const updateData: Record<string, unknown> = {};
  if (formData.name !== undefined) updateData.name = formData.name;
  if (formData.slug !== undefined) updateData.slug = formData.slug;
  if (formData.description !== undefined)
    updateData.description = formData.description || null;
  if (formData.avatarUrl !== undefined)
    updateData.avatar_url = formData.avatarUrl || null;
  if (formData.defaultModel !== undefined)
    updateData.default_model = formData.defaultModel || null;
  if (formData.isActive !== undefined) updateData.is_active = formData.isActive;
  if (formData.metadata !== undefined) updateData.metadata = formData.metadata;

  const { data, error } = await supabase
    .from("ai_personas" as any)
    .update(updateData)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  if (!data) throw new Error("No data returned after update");
  return transformPersona(data);
};

export const toggleAiPersonaActive = async (
  id: number,
  isActive: boolean
): Promise<AiPersona> => {
  return updateAiPersona(id, { isActive });
};

export const deleteAiPersona = async (id: number): Promise<void> => {
  // Safety check: don't delete if there are active templates linked
  const { data: activeTemplates } = await supabase
    .from("ai_prompt_templates" as any)
    .select("id")
    .eq("persona_id", id)
    .eq("is_active", true)
    .limit(1);

  if (activeTemplates && activeTemplates.length > 0) {
    throw new Error(
      "Cannot delete a persona with active templates. Deactivate or reassign templates first."
    );
  }

  const { error } = await supabase
    .from("ai_personas" as any)
    .delete()
    .eq("id", id);

  if (error) throw error;
};
